/**
 * Microfones que o Chrome enxerga neste computador (spec 016).
 *
 * Nada aqui depende de marca ou modelo: a lista vem do `enumerateDevices` e
 * o nome é só o que o Windows mostra, pra exibir. A identidade é o
 * `deviceId`, que o Chrome mantém fixo por site e perfil (muda se os dados
 * do site forem limpos — aí `store.resolveMics` reencontra pelo nome).
 *
 * O Chrome no Windows lista o mesmo aparelho mais de uma vez: "Padrão – …"
 * (`deviceId` 'default') e "Comunicações – …" ('communications') são
 * apelidos de um microfone real que também aparece com o próprio id. Eles
 * saem da lista.
 */

export interface MicDevice {
  /** `deviceId` do Chrome. */
  id: string;
  /** Nome que o Windows dá ("Microfone (Intelbras CAP 200 BT)"). */
  nome: string;
  /** Driver de áudio virtual (Voicemeeter, cabos): não é microfone de verdade. */
  virtual: boolean;
  /** O Chrome não deu o nome (falta a permissão de microfone pro site). */
  semNome?: true;
}

/** O pedaço do `MediaDeviceInfo` que interessa (facilita testar). */
export interface DeviceInfoLike {
  deviceId: string;
  kind: string;
  label: string;
}

const APELIDOS = new Set(['default', 'communications']);

/**
 * Drivers virtuais conhecidos. Erro aqui só esconde/mostra um item — a tela
 * tem "mostrar" pros escondidos. "Line In" (entrada de linha de verdade) não
 * casa: só "Line 1", "Line 2"… do Virtual Audio Cable.
 */
const VIRTUAL_RE =
  /voicemeeter|vb-audio|\bcable (output|input)\b|virtual audio|virtual cable|\bline \d+\b|voicemod|stereo mix|mixagem est[eé]reo/i;

export function isVirtualName(nome: string): boolean {
  return VIRTUAL_RE.test(nome);
}

/** Só entradas de áudio, sem apelidos e sem repetidos; nome vazio (sem permissão) vira genérico. */
export function normalizeDevices(list: readonly DeviceInfoLike[]): MicDevice[] {
  const vistos = new Set<string>();
  const out: MicDevice[] = [];
  for (const d of list) {
    if (d.kind !== 'audioinput' || !d.deviceId || APELIDOS.has(d.deviceId) || vistos.has(d.deviceId)) continue;
    vistos.add(d.deviceId);
    const rotulo = d.label.trim();
    const nome = rotulo || `Microfone ${out.length + 1}`;
    out.push({ id: d.deviceId, nome, virtual: isVirtualName(nome), ...(rotulo ? {} : { semNome: true as const }) });
  }
  return out;
}

/** Lista atual. Sem permissão de microfone o Chrome devolve nomes (e às vezes ids) vazios. */
export async function listMics(): Promise<MicDevice[]> {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!md?.enumerateDevices) return [];
  try {
    return normalizeDevices(await md.enumerateDevices());
  } catch {
    return [];
  }
}

/** Avisa quando um aparelho é ligado ou desligado. Devolve o "parar de ouvir". */
export function onMicsChange(cb: () => void): () => void {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!md?.addEventListener) return () => {};
  md.addEventListener('devicechange', cb);
  return () => md.removeEventListener('devicechange', cb);
}
